import { ApiResponseError } from "../../../apiError";
import { fetchAdministrationOrganization, fetchAdministrationOrganizations } from "../../api/administrationDashboard";
import { createKeyedResourceLoader } from "./keyedResourceLoader";
import { AdministrationSlice, OrganizationsSlice, errorMessage } from "./types";

const requests = new Map<string, Promise<void>>();
const detailLoader = createKeyedResourceLoader();
let generation = 0;
export const organizationsInitialState = {
  organizationsPage: null, organizationDetails: {}, organizationDetailLoading: {}, organizationDetailErrors: {},
  organizationsQuery: { q: "", states: [], sort: "priority" as const, metric: "" as const, limit: 25, offset: 0 },
  organizationsHydrated: false, organizationsLoading: false, organizationsError: "",
};
export const resetOrganizationsRequests = () => { generation += 1; requests.clear(); detailLoader.clear(); };
export const createOrganizationsSlice: AdministrationSlice<OrganizationsSlice> = (set, get) => ({
  ...organizationsInitialState,
  loadOrganizations: (query = {}, force = false) => {
    const nextQuery = { ...get().organizationsQuery, ...query };
    const key = JSON.stringify(nextQuery);
    if (!force && key === JSON.stringify(get().organizationsQuery) && get().organizationsHydrated) return Promise.resolve();
    const pending = requests.get(key); if (pending) return pending;
    const currentGeneration = ++generation;
    set({ organizationsQuery: nextQuery, organizationsLoading: true, organizationsError: "" });
    const request = fetchAdministrationOrganizations(nextQuery)
      .then((organizationsPage) => { if (currentGeneration === generation) set({ organizationsPage, organizationsHydrated: true }); })
      .catch((error) => { if (currentGeneration === generation) set({ organizationsError: errorMessage(error, "Unable to load organizations.") }); throw error; })
      .finally(() => { requests.delete(key); if (currentGeneration === generation) set({ organizationsLoading: false }); });
    requests.set(key, request); return request;
  },
  loadOrganizationDetail: (id, force = false) => detailLoader.load({
    key: id, force, cached: Boolean(get().organizationDetails[id]), request: () => fetchAdministrationOrganization(id).catch((error) => {
      if (error instanceof ApiResponseError && error.status === 404) {
        set((state) => {
          const { [id]: removed, ...organizationDetails } = state.organizationDetails;
          return { organizationDetails };
        });
        throw new ApiResponseError("Organization not found. It may have been deleted.", 404);
      }
      throw error;
    }),
    onStart: () => set((state) => ({ organizationDetailLoading: { ...state.organizationDetailLoading, [id]: true }, organizationDetailErrors: { ...state.organizationDetailErrors, [id]: "" } })),
    onSuccess: (detail) => set((state) => ({ organizationDetails: { ...state.organizationDetails, [id]: detail } })),
    onError: (error) => set((state) => ({ organizationDetailErrors: { ...state.organizationDetailErrors, [id]: error } })),
    onFinish: () => set((state) => ({ organizationDetailLoading: { ...state.organizationDetailLoading, [id]: false } })), errorMessage: "Unable to load organization.",
  }),
});
